import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  IconButton,
  Divider,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";

const ServiceStructureDialog = ({ open, onClose, onSave, initialData }) => {
  const [structure, setStructure] = useState({
    id: "",
    categories: [],
  });

  // Charger une structure existante ou initialiser une nouvelle
  useEffect(() => {
    console.log("Initial data received:", initialData);

    if (initialData && initialData.categories) {
      setStructure({
        type: "serviceStructure",
        id: initialData.id || `service-${Date.now()}`,
        categories: [...initialData.categories], // Assurer une copie des catégories
      });
    } else {
      setStructure({
        id: `service-${Date.now()}`,
        categories: [],
        type: "serviceStructure",
      });
    }
  }, [initialData]);

  // Ajouter une catégorie
  const addCategory = () => {
    setStructure((prev) => ({
      ...prev,
      categories: [
        ...prev.categories,
        { name: "", description: "", services: [] },
      ],
    }));
  };

  // Modifier une catégorie
  const updateCategory = (index, key, value) => {
    const updatedCategories = [...structure.categories];
    updatedCategories[index][key] = value;
    setStructure((prev) => ({ ...prev, categories: updatedCategories }));
  };

  // Supprimer une catégorie
  const removeCategory = (index) => {
    setStructure((prev) => ({
      ...prev,
      categories: prev.categories.filter((_, i) => i !== index),
    }));
  };

  // Ajouter un service dans une catégorie
  const addService = (categoryIndex) => {
    const updatedCategories = [...structure.categories];
    updatedCategories[categoryIndex].services.push({
      id: `service-${Date.now()}`,
      name: "",
      duration: "",
      price: "",
    });
    setStructure((prev) => ({ ...prev, categories: updatedCategories }));
  };

  // Modifier un service
  const updateService = (catIndex, servIndex, key, value) => {
    const updatedCategories = [...structure.categories];
    updatedCategories[catIndex].services[servIndex][key] = value;
    setStructure((prev) => ({ ...prev, categories: updatedCategories }));
  };

  // Supprimer un service
  const removeService = (catIndex, servIndex) => {
    const updatedCategories = [...structure.categories];
    updatedCategories[catIndex].services = updatedCategories[
      catIndex
    ].services.filter((_, i) => i !== servIndex);
    setStructure((prev) => ({ ...prev, categories: updatedCategories }));
  };

  // Sauvegarder
  const handleSave = () => {
    onSave({
      ...structure,
      type: "serviceStructure",
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {initialData ? "Modifier la structure" : "Ajouter une structure"}
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          label="ID"
          fullWidth
          margin="dense"
          value={structure.id}
          disabled
          sx={{ mb: 3 }}
        />

        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={addCategory}
          sx={{ mb: 3, display: "block" }}>
          Ajouter une catégorie
        </Button>

        {structure.categories.map((category, catIndex) => (
          <div key={catIndex}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 2 }}>
              Catégorie {catIndex + 1}
            </Typography>

            {/* Category Inputs */}
            <TextField
              label="Nom de la catégorie"
              fullWidth
              margin="dense"
              value={category.name}
              onChange={(e) => updateCategory(catIndex, "name", e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={2}
              margin="dense"
              value={category.description}
              onChange={(e) =>
                updateCategory(catIndex, "description", e.target.value)
              }
              sx={{ mb: 2 }}
            />

            {/* Delete Category Button */}
            <IconButton
              color="error"
              onClick={() => removeCategory(catIndex)}
              sx={{ position: "absolute", top: "8px", right: "8px" }}>
              <Delete />
            </IconButton>

            {/* Add Service Button */}
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => addService(catIndex)}
              sx={{ mt: 2 }}>
              Ajouter un service
            </Button>

            {/* Services for the Category */}
            {category.services.map((service, servIndex) => (
              <div
                key={servIndex}
                style={{
                  paddingLeft: "20px",
                  marginTop: "10px",
                  display: "flex",
                }}>
                <TextField
                  label="Nom du service"
                  fullWidth
                  margin="dense"
                  value={service.name}
                  onChange={(e) =>
                    updateService(catIndex, servIndex, "name", e.target.value)
                  }
                  sx={{ mb: 2, flex: "60" }}
                />
                <TextField
                  label="Durée"
                  fullWidth
                  margin="dense"
                  value={service.duration}
                  onChange={(e) =>
                    updateService(
                      catIndex,
                      servIndex,
                      "duration",
                      e.target.value
                    )
                  }
                  sx={{ mb: 1, flex: "20" }}
                />
                <TextField
                  label="Prix"
                  fullWidth
                  margin="dense"
                  value={service.price}
                  onChange={(e) =>
                    updateService(catIndex, servIndex, "price", e.target.value)
                  }
                  sx={{ mb: 2, flex: "20" }}
                />

                {/* Delete Service Button */}
                <IconButton
                  color="error"
                  onClick={() => removeService(catIndex, servIndex)}>
                  <Delete />
                </IconButton>
              </div>
            ))}
          </div>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Annuler
        </Button>
        <Button onClick={handleSave} color="primary" variant="contained">
          Enregistrer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ServiceStructureDialog;
